// Reine Rechenfunktionen fuer die Karte (IMG_4900 bis 4902, 4927 bis
// 4931, 01.09.2026): Sammelstand je Kreis und die Rot-Faerbung danach.

import type { Eintragskarte } from "./entries.js";
import type { Region } from "./regions.js";

export interface Fortschritt {
  gesehen: number;
  gesamt: number;
}

export function regionFortschritt(region: Region, eintraege: Eintragskarte): Fortschritt {
  return {
    gesehen: region.plateCodeIds.filter((id) => eintraege.has(id)).length,
    gesamt: region.plateCodeIds.length,
  };
}

/**
 * Die Kreisflaechen decken nicht ganz. In der Vorlage (IMG_4929, deutlich
 * zu sehen an "Osnabrueck" und den Fernstrassen quer durch das dunkle Rot)
 * scheinen Strassen, Fluesse und Ortsnamen der Basiskarte durch die
 * eingefaerbten Kreise hindurch. Mit voller Deckkraft waere alles, was
 * diese Karte an Untergrund mitbringt, genau in den Laendern unsichtbar,
 * in denen gesammelt wird.
 */
export const KREIS_DECKKRAFT = 0.75;

/**
 * Farbe eines Kreises nach Anteil gesehen, per Pipette aus IMG_4900
 * (01.09.2026): heller Rotton bei wenig, dunkler bei viel. Ein eigener,
 * kleinerer Rot-Verlauf, nicht die gruene "fortschritt"-Farbe vom
 * Hauptscreen - die Original-App faerbt die Karte rot, nicht gruen.
 *
 * Die Pipettenwerte stehen als Kommentar daneben, die Zahlen davor sind
 * herausgerechnet: gemessen wurde das FERTIGE Bild, gezeichnet wird mit
 * KREIS_DECKKRAFT ueber der Landfarbe. Wer den Verlauf anfasst, rechnet
 * dieselbe Runde: farbe = (pipette - (1 - deckkraft) * land) / deckkraft.
 */
const VERLAUF: [number, [number, number, number]][] = [
  [0, [248, 244, 240]], // ungesehen, bleibt die Landfarbe #F8F4F0
  [0.01, [245, 149, 140]], // ergibt #F6ADA5
  [0.5, [245, 125, 115]], // ergibt #F69B92
  [1, [244, 67, 53]], // ergibt #F56F64
];

/**
 * Farben der Basiskarte (Land, Grenzen, Seen), per Pipette aus IMG_4900.
 * Leaflet nimmt seine Farben als Zeichenkette entgegen, nicht als
 * CSS-Klasse - deshalb stehen sie hier und nicht nur in tailwind.config.js
 * unter `colors.karte`. Wer eine aendert, aendert beide Stellen; die
 * Landfarbe ist zusaetzlich der Nullwert des Rot-Verlaufs oben.
 */
export const BASISKARTE_STIL = {
  land: { fillColor: "#F8F4F0", fillOpacity: 1, color: "#9AA0A6", weight: 0.8 },
  binnengrenze: { color: "#D6D1CB", weight: 0.7, fill: false },
  // Binnengewaesser sind in der Vorlage kraeftiger als das Meer (#A0C8F0
  // gegen #BFD9F2). Mit der Meeresfarbe verschwand jeder See auf der
  // hellen Landflaeche fast.
  see: { fillColor: "#A0C8F0", fillOpacity: 1, color: "#A0C8F0", weight: 0.5 },
  fluss: { color: "#A0C8F0", weight: 0.9, fill: false },
  strasse: { color: "#EAB383", weight: 1, fill: false },
} as const;

export function farbeFuer(anteil: number): string {
  const a = Math.min(1, Math.max(0, anteil));
  for (let i = 1; i < VERLAUF.length; i++) {
    const [von, rgbVon] = VERLAUF[i - 1]!;
    const [bis, rgbBis] = VERLAUF[i]!;
    if (a <= bis) {
      const t = bis === von ? 1 : (a - von) / (bis - von);
      const mischen = (i2: 0 | 1 | 2) => Math.round(rgbVon[i2] + (rgbBis[i2] - rgbVon[i2]) * t);
      return `rgb(${mischen(0)}, ${mischen(1)}, ${mischen(2)})`;
    }
  }
  const [, letzte] = VERLAUF[VERLAUF.length - 1]!;
  return `rgb(${letzte[0]}, ${letzte[1]}, ${letzte[2]})`;
}
